/**
 * Option B — narrow checkout-gateway client (session → click → lead → order).
 * Enabled when PaymentEngine config sets checkoutGatewayURL.
 */
import $ from 'jquery';
import { getConfig } from './config';
import { debug } from './utils';
import { getTurnstileToken, isTurnstileEnabled } from './turnstile';
import {
	acquireToken,
	resolveMode,
	saveSessionMode,
	readSessionMode,
	clearSessionMode,
} from './turnstilePolicy';

const GW_SESSION_KEY = 'checkoutGatewaySessionId';
const GW_IDEMPOTENCY_KEY = 'checkoutOrderIdempotencyKey';

// Holds the in-flight session-init request so concurrent callers reuse it
// instead of each POSTing /session and creating duplicate gateway sessions.
let sessionPromise = null;

export function useCheckoutGateway() {
	return Boolean(String(getConfig().checkoutGatewayURL || '').trim());
}

function gatewayBase() {
	return String(getConfig().checkoutGatewayURL || '').replace(/\/$/, '');
}

function siteHost() {
	return window.location.host || window.location.hostname;
}

function campaignId() {
	const products = getConfig().products || [];
	return String((products[0] && products[0].campaignID_konnektive) || '');
}

export function getGatewaySessionId() {
	return sessionStorage.getItem(GW_SESSION_KEY) || '';
}

export function clearGatewaySession() {
	sessionStorage.removeItem(GW_SESSION_KEY);
	clearSessionMode(sessionStorage);
	sessionPromise = null;
}

/**
 * Turnstile mode for the current gateway session. The gateway's per-campaign
 * policy wins; without it (older gateway) fall back to local turnstileEnabled.
 */
export function getTurnstileMode() {
	return resolveMode(readSessionMode(sessionStorage), isTurnstileEnabled());
}

/**
 * Stable per-attempt idempotency key for order POSTs. Minted once and reused
 * across retries so the gateway can dedupe a resubmitted order; cleared once a
 * definitive response (success or decline) is received so a genuinely new
 * attempt gets a fresh key.
 */
export function getOrderIdempotencyKey(prefix) {
	let key = sessionStorage.getItem(GW_IDEMPOTENCY_KEY);
	if (!key) {
		key = `${prefix || 'order'}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
		sessionStorage.setItem(GW_IDEMPOTENCY_KEY, key);
	}
	return key;
}

export function clearOrderIdempotencyKey() {
	sessionStorage.removeItem(GW_IDEMPOTENCY_KEY);
}

/**
 * Ensure a server-side checkout session exists. Returns gateway sessionId.
 */
export function ensureGatewaySession() {
	const existing = getGatewaySessionId();
	if (existing) {
		return $.Deferred().resolve(existing).promise();
	}
	if (sessionPromise) {
		return sessionPromise;
	}
	const body = {
		siteHost: siteHost(),
		campaignId: campaignId(),
	};
	debug('checkout-gateway session init: ' + JSON.stringify(body));
	sessionPromise = $.ajax({
		url: `${gatewayBase()}/session`,
		method: 'POST',
		contentType: 'application/json',
		data: JSON.stringify(body),
		xhrFields: { withCredentials: true },
		timeout: 15000,
	}).then((data) => {
		if (!data || !data.sessionId) {
			sessionPromise = null;
			return $.Deferred().reject(data || { message: 'No gateway sessionId' }).promise();
		}
		sessionStorage.setItem(GW_SESSION_KEY, data.sessionId);
		saveSessionMode(sessionStorage, data.turnstile);
		debug('checkout-gateway turnstile mode: ' + getTurnstileMode());
		sessionPromise = null;
		return data.sessionId;
	}, (err) => {
		sessionPromise = null;
		return $.Deferred().reject(err).promise();
	});
	return sessionPromise;
}

/**
 * POST JSON to checkout-gateway with X-Session-Id.
 * Orders attach X-Turnstile-Token per the session's Turnstile mode:
 * on = required token (no silent skip; only an active ts-bypass sends none),
 * watch = best effort, off = never loaded.
 * Credentials included so the API can apply session cookies when present.
 *
 * options.existingSessionOnly (G-8): steps that act on the order this
 * checkout already placed (upsell, gift, thank-you /finish) must never open a
 * new session — a fresh session has no order, so the call could only fail or,
 * worse, act on nothing the shopper bought. Without a stored session the
 * request is not sent and the promise rejects with { status: 0, noSession }.
 */
export function gatewayRequest(path, body, extraHeaders, options) {
	const op = String(path || '').replace(/^\//, '');

	if (options && options.existingSessionOnly && !getGatewaySessionId()) {
		debug('checkout-gateway ' + op + ': no checkout session; not sent');
		return $.Deferred().reject({ status: 0, noSession: true, message: 'No checkout session' }).promise();
	}

	// The mode arrives with the session, so resolve the session before Turnstile.
	return ensureGatewaySession().then((sessionId) => {
		const mode = op === 'order' ? getTurnstileMode() : 'off';
		// Bridge the native promise into a jQuery Deferred: sites load jQuery 1.12
		// (not Promises/A+, $.when would resolve with the Promise object itself) or 3.x,
		// and callers need $.ajax's (data, status, xhr) args on .done/.fail.
		const tokenDeferred = $.Deferred();
		acquireToken(mode, () => getTurnstileToken({ action: 'checkout' }), { debug })
			.then(tokenDeferred.resolve, tokenDeferred.reject);
		return tokenDeferred.promise()
			.then((turnstileToken) => {
				const headers = {
					'Content-Type': 'application/json',
					'X-Session-Id': sessionId,
					...(extraHeaders || {}),
				};
				if (turnstileToken) {
					headers['X-Turnstile-Token'] = turnstileToken;
				}
				const payload = { ...(body || {}), sessionId };
				if (turnstileToken) {
					payload.turnstileToken = turnstileToken;
				}
				return $.ajax({
					url: `${gatewayBase()}/${op}`,
					method: 'POST',
					contentType: 'application/json',
					data: JSON.stringify(payload),
					headers,
					xhrFields: { withCredentials: true },
					timeout: 30000,
				});
			});
	});
}
